import { ReloadOutlined } from '@ant-design/icons';
import { Popconfirm } from 'antd';
import { generatePath } from 'react-router-dom';

import SettingsIcon from '@asset/settings.icon.svg?react';
import { Button } from '@atom/button/button';
import { ButtonTypeEnum } from '@atom/button/button.enum';
import { ReadMore } from '@atom/read-more/read-more';
import { ServiceIcon } from '@atom/service-icon/service-icon';
import { Text } from '@atom/typography/typography';
import { IntegrationStateEnum } from '@enum/integration-state.enum';
import { RouteEnum } from '@enum/route.enum';
import { ColorEnum } from '@enum/style/color.enum';
import { useMemberRole } from '@hook/memeber/use-member-role.hook';
import { ConnectionInterface, ConnectionStateEnum } from '@interface/connection/connection.interface';
import { isExist } from '@util/data/is-exist';

import { IntegrationBadge } from './integration-badge/integration-badge';
import { useIntegrationCard } from './integration-card.hook';
import {
  Card,
  CardFooter,
  CardTitle,
  ColCard,
  ContentWrapper,
  Divider,
  IntegrationButton,
  Tag,
  TitleWrapper,
} from './integration-card.style';
import { IntegrationModal } from './integration-modal/integration-modal';

interface IntegrationCardProps {
  connection: ConnectionInterface;
  currentState: ConnectionStateEnum;
}

export const IntegrationCard = ({ connection, currentState }: IntegrationCardProps) => {
  const { description, name, serviceId, onOpenModal, isConnected, isShowed, integration, removeConnection, isLoading } =
    useIntegrationCard(connection, currentState);

  const { isOwner, isAdmin } = useMemberRole();

  if (!isExist(integration)) return null;

  return (
    <ColCard showed={isShowed} span={8}>
      <Card
        bordered={false}
        title={
          <TitleWrapper>
            <ServiceIcon height={40} icon={connection.serviceId} width={40} />
            {(isOwner || isAdmin) && currentState === ConnectionStateEnum.Added && (
              <Popconfirm
                cancelText='No'
                description='Are you sure to reset this connection?'
                okText='Yes'
                placement='left'
                title='Reset the connection'
                onConfirm={removeConnection}
              >
                <Button loading={isLoading} title='Reset connection' type={ButtonTypeEnum.Error}>
                  <ReloadOutlined />
                </Button>
              </Popconfirm>
            )}
          </TitleWrapper>
        }
      >
        <ContentWrapper>
          <Tag>{connection.unifiedApi.toUpperCase()}</Tag>
          <CardTitle
            title={name}
            description={
              <Text color={ColorEnum.Black70} ellipsis={{ rows: 3 }} mb={8} paragraph>
                {description}
              </Text>
            }
          />
          <ReadMore to={generatePath(RouteEnum.IntegrationView.path, connection)} />
        </ContentWrapper>
        <Divider />
        <CardFooter>
          <IntegrationBadge connection={connection} integration={integration} />
          {(isOwner || isAdmin) && integration.state !== IntegrationStateEnum.DISABLED && (
            <IntegrationButton
              icon={<SettingsIcon fill={ColorEnum.Teal100} width={24} />}
              type={ButtonTypeEnum.Link}
              onClick={onOpenModal(connection.serviceId)}
            >
              <Text color={ColorEnum.Inherit} level='m'>
                {isConnected ? 'Settings' : 'Connect'}
              </Text>
            </IntegrationButton>
          )}
        </CardFooter>
      </Card>
      <IntegrationModal serviceId={serviceId} onClose={onOpenModal(undefined)} />
    </ColCard>
  );
};
